import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { CBadge, CFormFeedback, CFormInput, CFormLabel, CPopover } from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { cilCopy } from '@coreui/icons';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { REGEX_START_SPACE } from '../../../constants';

const SimpleInputGroup = forwardRef((props, ref) => {
     const {
          id,
          name,
          label,
          placeholder,
          description,
          inputType,
          isRequired,
          isValidate,
          value,
          error,
          handleChange,
          disabled,
          onBlur,
          min,
          max,
          style,
          autoComplete,
          className,
          classNameInput,
          pattern,
          tooltip,
          badgeOnInput,
          textCopy,
          clickCopy,
          actionComponent,
     } = props;

     const inputRef = useRef(null);
     const hasSelectAllValue = useRef(false);

     const isInputTypeNumber = inputType === 'number';

     useImperativeHandle(
          ref,
          () => {
               return {
                    focus() {
                         inputRef.current.focus();
                    },
                    scrollIntoView() {
                         inputRef.current.scrollIntoView();
                    },
               };
          },
          []
     );

     const handleSelectValue = () => {
          const input = inputRef.current;

          // Check if the entire text is selected
          if (input.value.length > 0 && input.selectionStart === 0 && input.selectionEnd === input.value.length) {
               hasSelectAllValue.current = true;
          }
     };

     const handlePasteValue = (event) => {
          const text = event.clipboardData?.getData('Text');
          if (!text || disabled) return;

          setTimeout(() => {
               const finalText = text.trim();
               const newValue = value?.replace(REGEX_START_SPACE, '') || '';
               const finalValue = !!value && !hasSelectAllValue.current ? newValue + finalText : finalText;

               event.target.value = finalValue;
          }, 0);
     };

     // Listen for input values ​​starting with spaces
     useEffect(() => {
          if (!value || !inputRef.current?.value) return;

          const hasStartSpace = REGEX_START_SPACE.test(value);

          if (hasStartSpace) {
               const newValue = value.replace(REGEX_START_SPACE, '');

               inputRef.current.value = newValue;
          }
     }, [inputRef.current?.value]); // eslint-disable-line react-hooks/exhaustive-deps

     // Handle event listener wheel with type number
     useEffect(() => {
          if (!isInputTypeNumber) return;

          const handleEventListenerScrollTypeNumber = () => {
               const hasClassNoscroll = document.activeElement.classList.contains('noscroll');

               if (document.activeElement.type === 'number' && hasClassNoscroll) {
                    document.activeElement.blur();
               }
          };

          document.addEventListener('wheel', handleEventListenerScrollTypeNumber);

          return () => {
               document.removeEventListener('wheel', handleEventListenerScrollTypeNumber);
          };
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     return (
          <div
               className={classNames('form-group', {
                    [className]: className,
                    'input-group-is-invalid': error,
               })}
          >
               <div className="d-flex">
                    {label && (
                         <CFormLabel
                              htmlFor={id}
                              className={classNames({
                                   'd-flex align-items-center': actionComponent,
                              })}
                         >
                              {<span className="me-1">{label}</span>}
                              {tooltip && (
                                   <CPopover content={tooltip} placement="top" trigger="hover">
                                        <CIcon icon="icon-info-cilcle" />
                                   </CPopover>
                              )}
                              {isRequired && <span className="text-danger">*</span>}
                              {actionComponent && actionComponent}
                              {description && <span className="label-description">({description})</span>}
                         </CFormLabel>
                    )}

                    {clickCopy && (
                         <div className="ms-1">
                              <CIcon className="icon-copy" icon={cilCopy} onClick={clickCopy} />
                              {textCopy && <p className="text-copied">{textCopy}</p>}
                         </div>
                    )}
               </div>

               <div className={`input-block${badgeOnInput ? ' badge-on-input' : ''}`}>
                    <CFormInput
                         id={id}
                         ref={inputRef}
                         name={name || id}
                         type={inputType}
                         className={classNames({
                              [classNameInput]: classNameInput,
                              noscroll: isInputTypeNumber,
                         })}
                         invalid={(isRequired || isValidate) && !!error}
                         value={(typeof value !== 'undefined' && value !== null) || value ? value : ''}
                         disabled={disabled}
                         placeholder={placeholder}
                         max={max}
                         min={min}
                         style={style}
                         autoComplete={autoComplete}
                         pattern={pattern}
                         onChange={handleChange}
                         onBlur={onBlur}
                         onSelect={handleSelectValue}
                         onPaste={handlePasteValue}
                    />
                    {(isRequired || isValidate) && <CFormFeedback invalid>{error}</CFormFeedback>}

                    {badgeOnInput && <CBadge color="success">{badgeOnInput}</CBadge>}
               </div>
          </div>
     );
});

SimpleInputGroup.propTypes = {
     id: PropTypes.string,
     name: PropTypes.string,
     label: PropTypes.string,
     description: PropTypes.string,
     inputType: PropTypes.string,
     isRequired: PropTypes.bool,
     value: PropTypes.any,
     error: PropTypes.string,
     handleChange: PropTypes.func,
     disabled: PropTypes.bool,
     min: PropTypes.any,
     max: PropTypes.any,
     style: PropTypes.object,
     autoComplete: PropTypes.string,
     className: PropTypes.string,
     pattern: PropTypes.string,
     tooltip: PropTypes.string,
     textCopy: PropTypes.string,
     clickCopy: PropTypes.func,
     classNameInput: PropTypes.string,
     actionComponent: PropTypes.node,
};

SimpleInputGroup.defaultProps = {
     inputType: 'text',
     value: '',
     error: '',
     description: '',
     autoComplete: 'off',
};

export default SimpleInputGroup;
